/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {GetterTree} from 'vuex';
import {RootState} from '@/store/types';
import {
    SpeciesAchievementInterface,
    TrophyCabinetMode,
    TrophyGrade,
    TrophyStateInterface,
} from '@/store/modules/trophy-cabinet/types';
import {SpeciesInterface} from '@/store/modules/game/types';

/**
 * The trophy cabinet module getters provide entry points for stored data.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const getters: GetterTree<TrophyStateInterface, RootState> = {
    /**
     * Checks for loading state.
     * @param {TrophyStateInterface} state
     * @return {boolean}
     */
    loading(state: TrophyStateInterface): boolean {
        return state.loadStatus.loading;
    },

    /**
     * Checks for loading state failure.
     * @param {TrophyStateInterface} state
     * @return {boolean}
     */
    failed(state: TrophyStateInterface): boolean {
        return !state.loadStatus.loading && state.loadStatus.error;
    },

    /**
     * Checks for loading state success.
     * @param {TrophyStateInterface} state
     * @return {boolean}
     */
    loaded(state: TrophyStateInterface): boolean {
        return !state.loadStatus.loading && state.loadStatus.success;
    },

    /**
     * Gets the trophy state mode setting.
     * @param {TrophyStateInterface} state
     * @return {TrophyCabinetMode}
     */
    mode(state: TrophyStateInterface): TrophyCabinetMode {
        return state.mode;
    },

    /**
     * Gets the personal contribution to a species challenge.
     * @param {TrophyStateInterface} state
     * @return {(species: SpeciesInterface) => number}
     */
    contribution(state: TrophyStateInterface): (species: SpeciesInterface) => number {
        return (species: SpeciesInterface): number => {
            const achievement: SpeciesAchievementInterface|undefined = state.speciesAchievements.find(
                (item: SpeciesAchievementInterface) => {
                    return species['@id'] === item.species;
                });

            if (!achievement || !achievement[state.mode]) {
                return 0;
            }

            return +achievement[state.mode];
        };
    },

    /**
     * Gets the trophy grade for the species.
     * @param {TrophyStateInterface} state
     * @return {(species: SpeciesInterface) => TrophyGrade}
     */
    trophy(state: TrophyStateInterface): (species: SpeciesInterface) => TrophyGrade {
        return (species: SpeciesInterface): TrophyGrade => {
            const achievement: SpeciesAchievementInterface|undefined = state.speciesAchievements.find(
                (item: SpeciesAchievementInterface) => {
                return species['@id'] === item.species;
            });

            if (!achievement || !species.medals || !species.medals[state.mode]) {
                return TrophyGrade.NONE;
            }

            let value: number = 0;
            switch (state.mode) {
                case TrophyCabinetMode.BOUGHT:
                    value = achievement.bought;
                    break;
                case TrophyCabinetMode.BORN:
                    value = achievement.born;
                    break;
                case TrophyCabinetMode.SOLD:
                    value = achievement.sold;
                    break;
                case TrophyCabinetMode.RELEASED:
                    value = achievement.released;
                    break;
            }

            if (species.medals[state.mode].G <= value) {
                return TrophyGrade.GOLD;
            }

            if (species.medals[state.mode].S <= value) {
                return TrophyGrade.SILVER;
            }

            if (species.medals[state.mode].B <= value) {
                return TrophyGrade.BRONZE;
            }

            return TrophyGrade.NONE;
        };
    },
};
